import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Identity verification (KYC Yalola) — mirrors backend/kyc_service.py. Independent from the creator role.
export const IDENTITY_STATUS = {
  not_started: { key: "identity.status_not_started", fr: "Non commencée", bg: "#F3F4F6", color: "#374151" },
  in_progress: { key: "identity.status_in_progress", fr: "En cours", bg: "#DBEAFE", color: "#1E40AF" },
  pending_review: { key: "identity.status_pending_review", fr: "En cours d'examen", bg: "#FEF3C7", color: "#92400E" },
  verified: { key: "identity.status_verified", fr: "Vérifiée", bg: "#D1FAE5", color: "#065F46" },
  rejected: { key: "identity.status_rejected", fr: "Refusée", bg: "#FEE2E2", color: "#991B1B" },
  expired: { key: "identity.status_expired", fr: "Expirée", bg: "#FEE2E2", color: "#991B1B" },
  requires_resubmission: { key: "identity.status_requires_resubmission", fr: "Nouvelle pièce demandée", bg: "#FFEDD5", color: "#9A3412" },
};
export const identityMeta = (s) => IDENTITY_STATUS[s] || IDENTITY_STATUS.not_started;

// aspect = physical document ratio for the guide frame (ISO 7810 ID-1 cards 85.6×54 mm, ID-3 passport page 125×88 mm).
export const DOC_TYPES = [
  { value: "id_card", key: "identity.doc_id_card", fr: "Carte d'identité", descKey: "identity.doc_desc_id_card", descFr: "Carte nationale d'identité", aspect: 1.586 },
  { value: "passport", key: "identity.doc_passport", fr: "Passeport", descKey: "identity.doc_desc_passport", descFr: "Page avec ta photo", aspect: 1.42 },
  { value: "driver_license", key: "identity.doc_driver_license", fr: "Permis de conduire", descKey: "identity.doc_desc_driver_license", descFr: "Permis de conduire officiel", aspect: 1.586 },
  { value: "residence_permit", key: "identity.doc_residence_permit", fr: "Titre de séjour", descKey: "identity.doc_desc_residence_permit", descFr: "Titre ou carte de séjour", aspect: 1.586 },
];
export const docTypeLabel = (t, v) => { const d = DOC_TYPES.find((x) => x.value === v); return d ? t(d.key, d.fr) : (v || "—"); };
export const docAspect = (v) => (DOC_TYPES.find((x) => x.value === v) || {}).aspect || 1.586;
export const SIDES = { front: { key: "identity.side_front", fr: "Recto" }, back: { key: "identity.side_back", fr: "Verso" } };

// Client check codes (lib/captureAnalysis) → what to say and what to fix. Mirrors kyc_service.CLIENT_CHECK_ISSUES.
export const CAPTURE_ISSUES = {
  no_document: { key: "identity.issue_no_document", fr: "Aucun document détecté", fixKey: "identity.fix_no_document", fixFr: "Place le document dans le cadre." },
  too_dark: { key: "identity.issue_too_dark", fr: "Photo trop sombre", fixKey: "identity.fix_too_dark", fixFr: "Mets-toi dans un endroit plus lumineux." },
  too_bright: { key: "identity.issue_too_bright", fr: "Photo trop claire", fixKey: "identity.fix_too_bright", fixFr: "Évite la lumière directe et les reflets." },
  blurry: { key: "identity.issue_blurry", fr: "Image floue", fixKey: "identity.fix_blurry", fixFr: "Tiens l'appareil stable et attends la mise au point." },
  low_resolution: { key: "identity.issue_low_resolution", fr: "Résolution insuffisante", fixKey: "identity.fix_low_resolution", fixFr: "Rapproche le document ou utilise une meilleure caméra." },
  too_small: { key: "identity.issue_too_small", fr: "Rapproche le document", fixKey: "identity.fix_too_small", fixFr: "Le document doit remplir le cadre." },
  incomplete: { key: "identity.issue_incomplete", fr: "Document incomplet", fixKey: "identity.fix_incomplete", fixFr: "Les quatre coins doivent être visibles, sans découpe." },
};
export const issueText = (t, code) => { const m = CAPTURE_ISSUES[code]; return m ? { title: t(m.key, m.fr), fix: t(m.fixKey, m.fixFr) } : { title: code, fix: "" }; };

// Multipart upload to a KYC endpoint (never the generic /upload → no feed moderation, no public URL).
export const uploadKycFile = async (url, file, fields = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

// Documents are fetched with the session token and rendered from a blob URL: no document ever has a public URL.
export const useSecureDoc = (path) => {
  const [state, setState] = useState({ url: "", status: path ? "loading" : "idle" });
  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    let obj = "";
    setState({ url: "", status: "loading" });
    api.get(path, { responseType: "blob" })
      .then(({ data }) => { if (!alive) return; obj = URL.createObjectURL(data); setState({ url: obj, status: "ok" }); })
      .catch((e) => { if (alive) setState({ url: "", status: e?.response?.status === 410 ? "purged" : "error" }); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  return state;
};

// "kycdoc:{id}" refs stored in legacy role requests → member/admin endpoint path.
export const kycDocRef = (ref) => (typeof ref === "string" && ref.startsWith("kycdoc:") ? ref.slice(7) : null);
